import React, { forwardRef, useState } from 'react';
import styled from 'styled-components';
import 'react-stately';
import { useFocusRing } from 'react-aria';
import { useTheme } from '../../components/ThemeProvider/ThemeProvider.js';
import Box from '../../components/Box/Box.js';
import Button from '../../components/Button/Button.js';
import { get } from '../../constants.js';
import sx from '../../sx.js';

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
const getIsOnlySingleIcon = ({
  children,
  leadingIcon,
  trailingIcon
}) => {
  if (children) {
    return false;
  }
  if (leadingIcon && !trailingIcon) {
    return true;
  }
  if (!leadingIcon && trailingIcon) {
    return true;
  }
  return false;
};

/**
 * Use as a drop-in replacement for buttons in Appfire brand scenarios.
 *
 * Extends button ARIA primitive. Provides styles for various needs, normalized behavior across different platforms and devices, and consistent keyboard focus behavior.
 *
 */
const MktgButton = /*#__PURE__*/forwardRef(({
  appearance = 'primary',
  size = 'default',
  spacing = 'default',
  shouldFitContainer = false,
  isDisabled,
  leadingIcon,
  trailingIcon,
  overlay,
  testId,
  children,
  ariaLabel,
  UNSAFE_className,
  ...props
}, ref) => {
  console.warn('MktgButton is deprecated. It will be removed in a future release. Once the library that styled brand components ship from is announced, there will be at least 6 months before this component is removed from Fuegokit React.');
  const appearances = ['base', 'primary', 'secondary', 'hub', 'outline', 'outline-subtle', 'subtle', 'link', 'link-subtle'];
  const {
    colorMode,
    setColorMode
  } = useTheme();
  const {
    isFocusVisible,
    focusProps
  } = useFocusRing();
  // 'disable' input with a button when there is an overlay
  const hasOverlay = Boolean(overlay);
  const [isActive, setIsActive] = useState(false);
  let Component;
  if (appearances.includes(appearance)) {
    Component = StyledMktgButton;
  } else {
    throw new Error(`Unrecognized Button appearance: ${appearance}. Please choose from the available options for MktgButton: "base" | "primary" | "secondary" | "subtle" | "link-subtle" | "outline" | "outline-subtle"`);
  }
  const isOnlySingleIcon = getIsOnlySingleIcon({
    children,
    leadingIcon,
    trailingIcon
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Component, _extends({
    disabled: isDisabled,
    shouldFitContainer: shouldFitContainer,
    appearance: appearance,
    spacing: spacing,
    size: size,
    leadingIcon: leadingIcon,
    trailingIcon: trailingIcon,
    isOnlySingleIcon: isOnlySingleIcon,
    isActive: isActive,
    isFocusVisible: isFocusVisible,
    ariaLabel: isOnlySingleIcon ? ariaLabel : null,
    overlay: overlay,
    colorMode: colorMode,
    className: UNSAFE_className,
    ref: ref,
    "data-has-overlay": hasOverlay ? true : undefined,
    "data-testid": testId
  }, focusProps, props), overlay && /*#__PURE__*/React.createElement(Box, {
    as: "span",
    sx: {
      position: 'absolute',
      left: 0,
      top: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }
  }, overlay), overlay ? /*#__PURE__*/React.createElement(Box, {
    as: "span",
    sx: {
      opacity: 0
    }
  }, children) : children));
});
/*
  These Appfire Marketing theme values are expressed as functions so that if we decide to make
  them dependent on props in the future, it wouldn't require a significant
  refactor everywhere they are being used.
*/
const getBorderRadius = () => 99999;
const getGridSize = () => 8;
const getFontSize = () => 16;
const borderRadius = getBorderRadius();
const gridSize = getGridSize();
const fontSize = getFontSize();
const heights = {
  default: `${gridSize * 4 / fontSize}em`,
  // 32px
  compact: `${gridSize / fontSize}em`,
  none: 'auto'
};
const lineHeights = {
  default: heights.default,
  compact: heights.compact,
  none: 'inherit'
};
const padding = {
  // 12px gutter (10px, 2px for border)
  default: `${gridSize}px ${gridSize * 2 + 2}px`,
  small: `${gridSize / 2}px ${gridSize + gridSize / 4}px`,
  large: `${gridSize + 4}px ${gridSize * 3}px`,
  jumbo: `${gridSize * 2}px ${gridSize * 3 + gridSize}px`,
  auto: '0'
};
const verticalAlign = {
  default: 'middle',
  compact: 'middle',
  none: 'baseline'
};

// @TODO: deprecate and remove; these tokens have been removed from the default theme, so these styles won't render anymore

// all values are references to tokens
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const colors = {
  background: {
    base: {
      default: 'colors.mktg.btn.base.background.default',
      hover: 'colors.mktg.btn.base.background.hover',
      active: 'colors.mktg.btn.base.background.active',
      disabled: 'colors.mktg.btn.base.background.disabled',
      selected: 'colors.mktg.btn.base.background.selected',
      focusSelected: 'colors.mktg.btn.base.background.focusSelected'
    },
    primary: {
      default: 'linear-gradient(225deg, #a044b9, #e54074, #f96c2c, #e54074, #a044b9)',
      hover: 'linear-gradient(225deg, #a044b9, #e54074, #f96c2c, #e54074, #a044b9)',
      active: 'linear-gradient(225deg, #a044b9, #e54074, #f96c2c, #e54074, #a044b9)',
      disabled: 'colors.mktg.btn.primary.background.disabled',
      selected: 'linear-gradient(225deg, #a044b9, #e54074, #f96c2c, #e54074, #a044b9)',
      focusSelected: 'linear-gradient(225deg, #a044b9, #e54074, #f96c2c, #e54074, #a044b9)'
    },
    secondary: {
      default: `linear-gradient(225deg,#FFFFFF,#FFFFFF,#e54074,#f96c2c)`,
      hover: `linear-gradient(225deg,#090a0a,#151717,#e54074,#f96c2c)`,
      active: `linear-gradient(225deg,#090a0a,#151717,#e54074,#f96c2c)`,
      disabled: 'colors.mktg.btn.outline.background.disabled',
      selected: `linear-gradient(225deg, #a044b9, #e54074, #f96c2c, #e54074, #a044b9)`,
      focusSelected: `linear-gradient(225deg, #a044b9, #e54074, #f96c2c, #e54074, #a044b9)`
    },
    hub: {
      default: 'linear-gradient(225deg, rgb(112, 47, 129), rgb(160, 68, 185))',
      hover: 'colors.mktg.btn.hub.background.hover',
      active: 'colors.mktg.btn.hub.background.active',
      disabled: 'colors.mktg.btn.hub.background.disabled',
      selected: 'colors.mktg.btn.hub.background.selected',
      focusSelected: 'colors.mktg.btn.hub.background.focusSelected'
    },
    outline: {
      default: 'colors.mktg.btn.outline.background.default',
      hover: 'colors.mktg.btn.outline.background.hover',
      active: 'colors.mktg.btn.outline.background.active',
      disabled: 'colors.mktg.btn.outline.background.disabled',
      selected: 'colors.mktg.btn.outline.background.selected',
      focusSelected: 'colors.mktg.btn.outline.background.focusSelected'
    },
    'outline-subtle': {
      default: 'colors.mktg.btn.outlineSubtle.background.default',
      hover: 'colors.mktg.btn.outlineSubtle.background.hover',
      active: 'colors.mktg.btn.outlineSubtle.background.active',
      disabled: 'colors.mktg.btn.outlineSubtle.background.disabled',
      selected: 'colors.mktg.btn.outlineSubtle.background.selected',
      focusSelected: 'colors.mktg.btn.outlineSubtle.background.focusSelected'
    },
    subtle: {
      default: 'colors.mktg.btn.subtle.background.default',
      hover: 'colors.mktg.btn.subtle.background.hover',
      active: 'colors.mktg.btn.subtle.background.active',
      disabled: 'colors.mktg.btn.subtle.background.disabled',
      selected: 'colors.mktg.btn.subtle.background.selected',
      focusSelected: 'colors.mktg.btn.subtle.background.focusSelected'
    },
    link: {
      default: 'colors.mktg.btn.link.background.default',
      hover: 'colors.mktg.btn.link.background.hover',
      active: 'colors.mktg.btn.link.background.active',
      disabled: 'colors.mktg.btn.link.background.disabled',
      selected: 'colors.mktg.btn.link.background.selected',
      focusSelected: 'colors.mktg.btn.link.background.focusSelected'
    },
    'link-subtle': {
      default: 'colors.mktg.btn.linkSubtle.background.default',
      hover: 'colors.mktg.btn.linkSubtle.background.hover',
      active: 'colors.mktg.btn.linkSubtle.background.active',
      disabled: 'colors.mktg.btn.linkSubtle.background.disabled',
      selected: 'colors.mktg.btn.linkSubtle.background.selected',
      focusSelected: 'colors.mktg.btn.linkSubtle.background.focusSelected'
    }
  },
  boxShadowColor: {
    base: {
      focus: 'colors.mktg.btn.base.border.focus',
      offset: 'colors.mktg.btn.base.border.offset',
      focusSelected: 'colors.mktg.btn.base.border.focus'
    },
    primary: {
      focus: 'colors.mktg.btn.primary.border.focus',
      offset: 'colors.mktg.btn.primary.border.offset',
      focusSelected: 'colors.mktg.btn.primary.border.focus'
    },
    secondary: {
      focus: 'colors.mktg.btn.secondary.border.focus',
      offset: 'colors.mktg.btn.secondary.border.offset',
      focusSelected: 'colors.mktg.btn.secondary.border.focus'
    },
    hub: {
      focus: 'colors.mktg.btn.hub.border.focus',
      offset: 'colors.mktg.btn.hub.border.offset',
      focusSelected: 'colors.mktg.btn.hub.border.focus'
    },
    outline: {
      focus: 'colors.mktg.btn.outline.border.focus',
      offset: 'colors.mktg.btn.outline.border.offset',
      focusSelected: 'colors.mktg.btn.outline.border.focus'
    },
    'outline-subtle': {
      focus: 'colors.mktg.btn.outlineSubtle.border.focus',
      offset: 'colors.mktg.btn.outlineSubtle.border.offset',
      focusSelected: 'colors.mktg.btn.outlineSubtle.border.focus'
    },
    subtle: {
      focus: 'colors.mktg.btn.subtle.border.focus',
      offset: 'colors.mktg.btn.subtle.border.offset',
      focusSelected: 'colors.mktg.btn.subtle.border.focus'
    },
    link: {
      focus: 'colors.mktg.btn.link.border.focus',
      offset: 'colors.mktg.btn.link.border.offset',
      focusSelected: 'colors.mktg.btn.link.border.focus'
    },
    'link-subtle': {
      focus: 'colors.mktg.btn.linkSubtle.border.focus',
      offset: 'colors.mktg.btn.linkSubtle.border.offset',
      focusSelected: 'colors.mktg.btn.linkSubtle.border.focus'
    }
  },
  color: {
    base: {
      default: 'colors.mktg.btn.base.color.default',
      hover: 'colors.mktg.btn.base.color.hover',
      active: 'colors.mktg.btn.base.color.active',
      disabled: 'colors.mktg.btn.base.color.disabled',
      selected: 'colors.mktg.btn.base.color.selected',
      focusSelected: 'colors.mktg.btn.base.color.focusSelected'
    },
    primary: {
      default: 'colors.mktg.btn.primary.color.default',
      hover: 'colors.mktg.btn.primary.color.hover',
      active: 'colors.mktg.btn.primary.color.active',
      disabled: 'colors.mktg.btn.primary.color.disabled',
      selected: 'colors.mktg.btn.primary.color.selected',
      focusSelected: 'colors.mktg.btn.primary.color.focusSelected'
    },
    secondary: {
      default: 'colors.mktg.btn.secondary.color.default',
      hover: 'colors.mktg.btn.secondary.color.hover',
      active: 'colors.mktg.btn.secondary.color.active',
      disabled: 'colors.mktg.btn.secondary.color.disabled',
      selected: 'colors.mktg.btn.secondary.color.selected',
      focusSelected: 'colors.mktg.btn.secondary.color.focusSelected'
    },
    hub: {
      default: 'colors.mktg.btn.hub.color.default',
      hover: 'colors.mktg.btn.hub.color.hover',
      active: 'colors.mktg.btn.hub.color.active',
      disabled: 'colors.mktg.btn.hub.color.disabled',
      selected: 'colors.mktg.btn.hub.color.selected',
      focusSelected: 'colors.mktg.btn.hub.color.focusSelected'
    },
    outline: {
      default: 'colors.mktg.btn.outline.color.default',
      hover: 'colors.mktg.btn.outline.color.hover',
      active: 'colors.mktg.btn.outline.color.active',
      disabled: 'colors.mktg.btn.outline.color.disabled',
      selected: 'colors.mktg.btn.outline.color.selected',
      focusSelected: 'colors.mktg.btn.outline.color.focusSelected'
    },
    'outline-subtle': {
      default: 'colors.mktg.btn.outlineSubtle.color.default',
      hover: 'colors.mktg.btn.outlineSubtle.color.hover',
      active: 'colors.mktg.btn.outlineSubtle.color.active',
      disabled: 'colors.mktg.btn.outlineSubtle.color.disabled',
      selected: 'colors.mktg.btn.outlineSubtle.color.selected',
      focusSelected: 'colors.mktg.btn.outlineSubtle.color.focusSelected'
    },
    subtle: {
      default: 'colors.mktg.btn.subtle.color.default',
      hover: 'colors.mktg.btn.subtle.color.hover',
      active: 'colors.mktg.btn.subtle.color.active',
      disabled: 'colors.mktg.btn.subtle.color.disabled',
      selected: 'colors.mktg.btn.subtle.color.selected',
      focusSelected: 'colors.mktg.btn.subtle.color.focusSelected'
    },
    link: {
      default: 'colors.mktg.btn.link.color.default',
      hover: 'colors.mktg.btn.link.color.hover',
      active: 'colors.mktg.btn.link.color.active',
      disabled: 'colors.mktg.btn.link.color.disabled',
      selected: 'colors.mktg.btn.link.color.selected',
      focusSelected: 'colors.mktg.btn.link.color.focusSelected'
    },
    'link-subtle': {
      default: 'colors.mktg.btn.linkSubtle.color.default',
      hover: 'colors.mktg.btn.linkSubtle.color.hover',
      active: 'colors.mktg.btn.linkSubtle.color.active',
      disabled: 'colors.mktg.btn.linkSubtle.color.disabled',
      selected: 'colors.mktg.btn.linkSubtle.color.selected',
      focusSelected: 'colors.mktg.btn.linkSubtle.color.focusSelected'
    }
  },
  borderColor: {
    base: {
      default: 'colors.mktg.btn.base.border.default',
      disabled: 'colors.mktg.btn.base.border.disabled'
    },
    primary: {
      default: 'colors.mktg.btn.primary.border.default',
      disabled: 'colors.mktg.btn.primary.border.disabled'
    },
    secondary: {
      default: 'colors.mktg.btn.secondary.border.default',
      disabled: 'colors.mktg.btn.secondary.border.disabled'
    },
    hub: {
      default: 'colors.mktg.btn.hub.border.default',
      disabled: 'colors.mktg.btn.hub.border.disabled'
    },
    outline: {
      default: 'colors.mktg.btn.outline.border.default',
      disabled: 'colors.mktg.btn.outline.border.disabled'
    },
    'outline-subtle': {
      default: 'colors.mktg.btn.outlineSubtle.border.default',
      disabled: 'colors.mktg.btn.outlineSubtle.border.disabled'
    },
    subtle: {
      default: 'colors.mktg.btn.subtle.border.default',
      disabled: 'colors.mktg.btn.subtle.border.disabled'
    },
    link: {
      default: 'colors.mktg.btn.link.border.default',
      focus: 'colors.mktg.btn.link.border.focus',
      disabled: 'colors.mktg.btn.link.border.disabled'
    },
    'link-subtle': {
      default: 'colors.mktg.btn.linkSubtle.border.default',
      focus: 'colors.mktg.btn.linkSubtle.border.focus',
      disabled: 'colors.mktg.btn.linkSubtle.border.disabled'
    }
  }
};
const StyledMktgButton = styled(Button).withConfig({
  displayName: "MktgButton__StyledMktgButton",
  componentId: "sc-1ccop8j-0"
})(["align-items:baseline;border-width:0;box-sizing:border-box;display:inline-flex;font-style:normal;max-width:100%;position:relative;text-transform:uppercase;text-align:center;text-decoration:none;align-items:center;white-space:nowrap;user-select:none;font-weight:600;box-shadow:none;padding:", ";font-size:", ";border-radius:", ";line-height:", ";vertical-align:", ";justify-content:", ";width:", ";border:2px solid;background-image:", ";background-color:", ";background-position:100% 0;background-size:300% 100%;border-color:", ";color:", ";text-decoration-color:transparent;transition:background-position 0.4s ease-in-out,text-decoration 0.4s ease-in-out,color 0.4s ease-in-out,text-decoration-color 0.4s ease-in-out;&:hover{cursor:pointer;background-position:0;color:", ";background-color:", ";text-decoration:", ";text-decoration-color:", ";text-underline-offset:", ";text-decoration-thickness:", ";}&:active{outline:none;transition-duration:0s;background:", ";color:", ";}&:disabled{cursor:not-allowed;color:", ";background:", ";border-color:", ";text-decoration:none;}--focus-shadow:0 1px 2px 0 rgba(0,0,0,0.05);--focus-ring-offset-width:", ";--focus-ring-offset-color:", ";--focus-ring-color:", ";--focus-ring-offset-shadow:0 0 0 var(--focus-ring-offset-width) var(--focus-ring-offset-color);--focus-ring-shadow:0 0 0 calc(2px + var(--focus-ring-offset-width)) var(--focus-ring-color);&:focus,&:focus:not(:focus-visible):not(.focus-visible){outline:0;box-shadow:", ";}&:focus-visible{outline:0;box-shadow:", ";}&:active{outline:0;box-shadow:none;background:", ";}", ""], p => p.size && p.isOnlySingleIcon && p.size === 'large' ? '16px 16px' : p.size && p.isOnlySingleIcon && p.size === 'jumbo' ? '16px 16px' : p.size && p.isOnlySingleIcon && p.size === 'default' ? '12px 12px' : p.size && p.isOnlySingleIcon && p.size === 'small' ? '8px 8px' : p.size ? padding[p.size] : '12px 24px', p => p.size === 'small' ? `${fontSize - 2}px` : p.size === 'jumbo' ? `${fontSize + 2}px` : `${fontSize}px`, borderRadius + 'px' , p => p.spacing ? lineHeights[p.spacing] : lineHeights['default'], p => p.spacing ? verticalAlign[p.spacing] : 'middle', p => p.shouldFitContainer === true ? 'center' : '', p => p.shouldFitContainer === true ? '100%' : '', p => p.appearance && p.appearance === 'primary' || p.appearance === 'hub' ? colors.background[p.appearance].default : p.appearance && p.appearance === 'secondary' && p.colorMode === 'day' ? `linear-gradient(-125deg,#ffffff,#ffffff,#e54074,#f96c2c)` : p.appearance && p.appearance === 'secondary' && p.colorMode === 'night' ? `linear-gradient(-125deg,#000000,#000000,#e54074,#f96c2c)` : '', p => p.appearance ? get(colors.background[p.appearance].default) : '', p => p.appearance ? get(colors.borderColor[p.appearance].default) : get(colors.borderColor.primary.default), p => p.appearance ? get(colors.color[p.appearance].default) : get('colors.text.default'), p => p.appearance ? get(colors.color[p.appearance].hover) : '#FFFFFF', p => p.appearance ? get(colors.background[p.appearance].hover) : '', p => p.appearance && p.appearance === 'link' ? 'underline' : 'inherit', p => p.appearance && p.appearance === 'link' ? get(colors.borderColor[p.appearance].focus) : '#FFFFFF', p => p.appearance && p.appearance === 'link' ? '6px' : '', p => p.appearance && p.appearance === 'link' && p.size !== 'small' ? '2px' : '', p => p.appearance ? get(colors.background[p.appearance].active) : '', p => p.appearance ? get(colors.color[p.appearance].active) : '', p => p.disabled && p.appearance ? get(colors.color[p.appearance].disabled) : '', p => p.disabled && p.appearance ? get(colors.background[p.appearance].disabled) : '', p => p.disabled && p.appearance ? get(colors.borderColor[p.appearance].disabled) : '', p => p.size === 'large' ? '3px' : p.size === 'small' ? '1px' : '2px', p => p.appearance ? get(colors.boxShadowColor[p.appearance].offset) : 'red', p => p.appearance ? get(colors.boxShadowColor[p.appearance].focus) : 'red', p => p.isFocusVisible ? 'var(--focus-ring-offset-shadow), var(--focus-ring-shadow), var(--focus-shadow, 0 0 #0000)' : 'none', p => p.isFocusVisible ? 'var(--focus-ring-offset-shadow), var(--focus-ring-shadow), var(--focus-shadow, 0 0 #0000)' : 'none', p => p.appearance ? get(colors.background[p.appearance].active) : '', sx);
var MktgButton$1 = MktgButton;
MktgButton.displayName = 'MktgButton';

export { MktgButton$1 as default, getBorderRadius, getFontSize, getGridSize };
